import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const CalendarPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const pagePermissions = permissions.filter(p => 
    p.name && p.name.includes('calendar.')
  );

  if (pagePermissions.length === 0) return null;

  const viewPerm = pagePermissions.find(p => p.name?.includes('.view') && !p.name?.includes('.approve') && !p.name?.includes('.delete_permanent'));
  const editPerm = pagePermissions.find(p => p.name?.includes('.edit') && !p.name?.includes('.approve') && !p.name?.includes('.delete_permanent'));
  const deletePerm = pagePermissions.find(p => p.name?.includes('.delete') && !p.name?.includes('.approve') && !p.name?.includes('.delete_permanent'));

  return (
    <Box 
      sx={{ 
        mb: 2,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
        borderRadius: 2,
        overflow: 'hidden',
        background: 'white'
      }}
    >
      {/* Page Header */}
      <Box
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          cursor: 'pointer',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
          borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
          '&:hover': {
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CalendarTodayIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
            Calendar Page
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {isExpanded ? '▼' : '▶'}
        </Typography>
      </Box>

      {/* Permission Switches */}
      <Collapse in={isExpanded}>
        <Box sx={{ p: 2 }}>
          {/* View Permission */}
          {viewPerm && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              py: 1,
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
            }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  View
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Can view calendar page
                </Typography>
              </Box>
              <Switch
                checked={roleForm.permissions[viewPerm.id]?.read || false}
                onChange={(e) => {
                  const newPermissions = { ...roleForm.permissions };
                  if (!newPermissions[viewPerm.id]) {
                    newPermissions[viewPerm.id] = {};
                  }
                  newPermissions[viewPerm.id].read = e.target.checked;
                  setRoleForm({ ...roleForm, permissions: newPermissions });
                }}
                size="small"
              />
            </Box>
          )}

          {/* Edit Permission */}
          {editPerm && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              py: 1,
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
            }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Edit
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Can edit calendar data
                </Typography>
              </Box>
              <Switch
                checked={roleForm.permissions[editPerm.id]?.write || false}
                onChange={(e) => {
                  const newPermissions = { ...roleForm.permissions };
                  if (!newPermissions[editPerm.id]) {
                    newPermissions[editPerm.id] = {};
                  }
                  newPermissions[editPerm.id].write = e.target.checked;
                  setRoleForm({ ...roleForm, permissions: newPermissions });
                }}
                size="small"
              />
            </Box>
          )}

          {/* Delete Permission */}
          {deletePerm && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              py: 1
            }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Delete
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Can delete calendar data
                </Typography>
              </Box>
              <Switch
                checked={roleForm.permissions[deletePerm.id]?.delete || false}
                onChange={(e) => {
                  const newPermissions = { ...roleForm.permissions };
                  if (!newPermissions[deletePerm.id]) {
                    newPermissions[deletePerm.id] = {};
                  }
                  newPermissions[deletePerm.id].delete = e.target.checked;
                  setRoleForm({ ...roleForm, permissions: newPermissions });
                }}
                size="small"
              />
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default CalendarPermissions;
